import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase');
}

const AUTH_STORAGE_KEY = 'sb-auth-token';
const RECOVERY_TIMESTAMP_KEY = 'fv_auth_recovery_at';
const RECOVERY_COOLDOWN_MS = 3000;

export interface Profile {
  id: string;
  role: string;
}

/**
 * Detecta exclusivamente errores de refresh token / sesión inexistente.
 * Nunca incluye errores de permisos (RLS) ni "JWT expired" para no
 * cerrar sesiones que solo necesitan refresco.
 */
const isRefreshTokenError = (text?: string | null): boolean => {
  if (!text) return false;
  const msg = text.toLowerCase();
  return (
    msg.includes('invalid refresh token') ||
    msg.includes('refresh token not found') ||
    msg.includes('refresh_token_not_found') ||
    msg.includes('refresh token') && (msg.includes('not found') || msg.includes('invalid')) ||
    msg.includes('invalid_grant') ||
    msg.includes('auth session missing') ||
    msg.includes('session not found')
  );
};

const errorToText = (reason: unknown): string => {
  if (!reason) return '';
  if (typeof reason === 'string') return reason;
  const err = reason as { name?: string; message?: string; code?: string };
  const parts = [err?.name, err?.message, err?.code].filter(Boolean).join(' ');
  try {
    return `${parts} ${JSON.stringify(reason)}`;
  } catch {
    return parts;
  }
};

// ============================================================
// 1. RECUPERACIÓN TEMPRANA (registrada ANTES de crear el cliente)
// ============================================================

let recoveryInFlight = false;

/** Borra las claves de sesión de Supabase sin tocar el resto del storage. */
const purgeAuthStorage = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
    keys.forEach((key) => {
      if (key.startsWith(AUTH_STORAGE_KEY) || key.includes('supabase.auth')) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // storage no disponible: se ignora
  }
};

/**
 * Limpia una sesión local inválida y devuelve al usuario a la pantalla
 * de acceso correspondiente. No hace peticiones de red ni reintentos.
 */
const recoverFromInvalidSession = () => {
  if (recoveryInFlight) return;

  let lastRecovery = 0;
  try {
    lastRecovery = Number(sessionStorage.getItem(RECOVERY_TIMESTAMP_KEY) || '0');
  } catch {
    lastRecovery = 0;
  }
  if (Date.now() - lastRecovery < RECOVERY_COOLDOWN_MS) return;

  recoveryInFlight = true;
  try {
    sessionStorage.setItem(RECOVERY_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // storage no disponible: se ignora
  }

  console.warn('[auth] Sesión local inválida detectada. Se limpian las credenciales.');

  purgeAuthStorage();

  const path = window.location.pathname;
  const needsAdminLogin = path.includes('/admin') && !path.includes('/admin/login');
  const needsClientPortal = path.includes('/client') && !path.includes('/client/portal');

  window.dispatchEvent(new Event('fv:auth-reset'));

  window.setTimeout(() => {
    try {
      const navigate = window.REACT_APP_NAVIGATE;
      if (typeof navigate === 'function') {
        if (needsAdminLogin) navigate('/admin/login', { replace: true });
        else if (needsClientPortal) navigate('/client/portal', { replace: true });
      }
    } catch {
      // navegación no disponible: los guards vuelven a evaluar la sesión
    }
    recoveryInFlight = false;
  }, 80);
};

// Rechazo no capturado (aquí llega el throw interno de GoTrueClient)
window.addEventListener('unhandledrejection', (event) => {
  const text = errorToText(event.reason);
  if (isRefreshTokenError(text)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    recoverFromInvalidSession();
  }
});

// Error síncrono (por si algún runtime lo reporta por esta vía)
window.addEventListener('error', (event) => {
  const text = errorToText((event as ErrorEvent).error) || event.message || '';
  if (isRefreshTokenError(text)) {
    recoverFromInvalidSession();
  }
});

/**
 * Limpieza preventiva: si la sesión guardada está incompleta o corrupta
 * (sin access_token o sin refresh_token), se descarta antes de que el
 * cliente intente refrescarla contra el servidor.
 */
const sanitizeStoredSession = () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return;
    }

    const root = parsed as Record<string, unknown> | null;
    const session = ((root?.currentSession ?? root) ?? null) as Record<string, unknown> | null;
    const accessToken = session?.access_token;
    const refreshToken = session?.refresh_token;

    const validAccess = typeof accessToken === 'string' && accessToken.length > 0;
    const validRefresh = typeof refreshToken === 'string' && refreshToken.length > 0;

    if (!validAccess || !validRefresh) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch {
    // storage no disponible: se ignora
  }
};

sanitizeStoredSession();

// ============================================================
// 2. CLIENTE ÚNICO
// ============================================================

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: AUTH_STORAGE_KEY,
  },
});

// ============================================================
// 3. LISTENER DE AUTH (SIEMPRE SÍNCRONO)
// ============================================================

supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('✅ Token refrescado correctamente');
  }
  if (event === 'SIGNED_OUT') {
    console.log('🚪 Sesión cerrada');
  }
  // El callback debe ser síncrono: cualquier limpieza se difiere.
  if (event === 'USER_UPDATED' && !session) {
    window.setTimeout(recoverFromInvalidSession, 0);
  }
});

// ============================================================
// 4. INTERCEPTOR DE FETCH
// ============================================================

/**
 * Errores de TRANSPORTE (la conexión se cerró sin respuesta HTTP).
 * net::ERR_CONNECTION_CLOSED llega aquí como TypeError "Failed to fetch".
 * Se reintenta UNA sola vez, solo en lecturas (GET/HEAD), porque un POST
 * podría haberse ejecutado ya en el servidor.
 */
const TRANSPORT_ERROR_PATTERN =
  /failed to fetch|networkerror|network error|load failed|connection closed|err_connection|err_network|econnreset|econnaborted|socket hang up|fetch failed/i;

const isTransportError = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string } | null;
  const text = `${err?.name ?? ''} ${err?.message ?? ''}`;
  return TRANSPORT_ERROR_PATTERN.test(text);
};

const isReadOnlyRequest = (args: unknown[]): boolean => {
  const init = args[1] as { method?: string } | undefined;
  const method = (init?.method ?? 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD';
};

const transportBackoff = (): Promise<void> =>
  new Promise((resolve) => {
    const jitter = 250 + Math.round(Math.random() * 250);
    setTimeout(resolve, jitter);
  });

const originalFetch = window.fetch;
window.fetch = async (...args) => {
  try {
    const response = await originalFetch(...args);

    if (args[0]?.toString().includes(supabaseUrl) && (response.status === 400 || response.status === 401)) {
      const clonedResponse = response.clone();
      try {
        const data = await clonedResponse.json();
        if (isRefreshTokenError(JSON.stringify(data))) {
          recoverFromInvalidSession();
        }
      } catch {
        // Ignorar errores al parsear JSON
      }
    }

    return response;
  } catch (error: unknown) {
    if (isRefreshTokenError(errorToText(error))) {
      recoverFromInvalidSession();
    }

    // Reintento único ante conexión cerrada (ERR_CONNECTION_CLOSED),
    // solo para lecturas y solo contra Supabase.
    const isSupabaseRequest = args[0]?.toString().includes(supabaseUrl) === true;
    if (isSupabaseRequest && isTransportError(error) && isReadOnlyRequest(args)) {
      await transportBackoff();
      return originalFetch(...args);
    }

    throw error;
  }
};

// ============================================================
// 5. VERIFICACIÓN PROACTIVA AL CARGAR
// ============================================================

(async () => {
  try {
    const { error } = await supabase.auth.getSession();
    if (error && isRefreshTokenError(error.message)) {
      recoverFromInvalidSession();
    }
  } catch (e: unknown) {
    if (isRefreshTokenError(errorToText(e))) {
      recoverFromInvalidSession();
    }
  }
})();